import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CheckCircle2, ArrowRight, Building2, Phone, Mail, Check } from 'lucide-react';
import { mockData } from '../mockData';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = 'https://calendly.com/brenz990/30min?month=2025-10';

export const NewLandingPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [estimateEmail, setEstimateEmail] = useState('');
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isClientLoginOpen, setIsClientLoginOpen] = useState(false);
  const [isEstimateOpen, setIsEstimateOpen] = useState(false);
  const [isChecklistThankYouOpen, setIsChecklistThankYouOpen] = useState(false);
  const [isEstimateThankYouOpen, setIsEstimateThankYouOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChecklistSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/checklist`, { email });
      if (response.data.success) {
        setEmail('');
        toast.success('Checklist sent to your email.');
        setTimeout(() => {
          setIsCalendlyOpen(true);
        }, 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClientLoginSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/client-portal-waitlist`, { email, name });
      if (response.data.success) {
        toast.success(`Thanks—We'll notify you at ${email} when the portal is live.`);
        setEmail('');
        setName('');
        setIsClientLoginOpen(false);
      }
    } catch (error) {
      toast.error('Something went wrong—please try again or email info@rockwellbookkeeping.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEstimateSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/estimate-request`, { email: estimateEmail });
      if (response.data.success) {
        setEstimateEmail('');
        setIsEstimateOpen(false);
        setIsEstimateThankYouOpen(true);
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <header className={`site-header ${isHeaderSticky ? 'sticky' : ''}`}>
        <div className="container-custom">
          <div className="header-content">
            <div className="logo">
              <Building2 className="logo-icon" />
              <span className="logo-text">{mockData.header.logo}</span>
            </div>
            <div className="header-actions">
              <Button 
                variant="ghost" 
                onClick={() => setIsClientLoginOpen(true)}
                className="client-login-btn"
              >
                {mockData.header.clientLoginText}
              </Button>
              <Button onClick={() => setIsCalendlyOpen(true)} className="cta-button">
                {mockData.header.ctaText}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <section id="hero" className="hero-section">
        <div className="hero-background"></div>
        <div className="container-custom">
          <div className="hero-content">
            <h1 className="hero-title">{mockData.hero.title}</h1>
            <p className="hero-subtitle">{mockData.hero.subtitle}</p>
            <div className="hero-cta">
              <Button onClick={() => setIsCalendlyOpen(true)} size="lg" className="cta-button cta-large">
                {mockData.hero.ctaPrimary}
                <ArrowRight className="ml-2" />
              </Button>
              <Button 
                onClick={() => setIsEstimateOpen(true)} 
                size="lg" 
                variant="outline" 
                className="cta-secondary cta-large"
              >
                {mockData.hero.ctaSecondary}
              </Button>
            </div>
            <div className="hero-support">
              <p className="support-text">{mockData.hero.supportLine}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="why-choose" className="why-choose-section">
        <div className="container-custom">
          <h2 className="section-title">{mockData.whyChoose.title}</h2>
          <div className="features-grid">
            {mockData.whyChoose.items.map((item, idx) => (
              <Card key={idx} className="feature-card glass-card">
                <CardHeader>
                  <Check className="check-icon" />
                  <CardTitle className="feature-title">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="feature-description">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="how-it-works-section">
        <div className="container-custom">
          <h2 className="section-title">{mockData.howItWorks.title}</h2>
          <div className="steps-grid">
            {mockData.howItWorks.steps.map((step, idx) => (
              <Card key={idx} className="step-card glass-card">
                <CardHeader>
                  <div className="step-number">{idx + 1}</div>
                  <CardTitle className="step-title">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="step-description">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="services-section">
        <div className="container-custom">
          <h2 className="section-title">{mockData.services.title}</h2>
          <div className="services-grid">
            {mockData.services.items.map((service, idx) => (
              <Card key={idx} className="service-card glass-card">
                <CardHeader>
                  <CardTitle className="service-title">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="service-description">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="resubmits" className="resubmits-section">
        <div className="container-custom">
          <Card className="resubmits-card glass-card">
            <CardHeader>
              <CardTitle className="resubmits-title">{mockData.resubmits.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="resubmits-description">{mockData.resubmits.description}</p>
              <Button onClick={() => document.getElementById('lead-magnet').scrollIntoView({ behavior: 'smooth' })} className="cta-button mt-4">
                {mockData.resubmits.ctaText}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <section id="about" className="about-section">
        <div className="container-custom">
          <h2 className="section-title">{mockData.about.title}</h2>
          <Card className="about-card glass-card">
            <CardContent className="about-content">
              <div className="founder-quote">"{mockData.about.quote}"</div>
              <div className="founder-credentials">{mockData.about.credentials}</div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section id="pricing" className="pricing-section">
        <div className="container-custom">
          <h2 className="section-title">{mockData.pricing.title}</h2>
          <p className="pricing-intro">{mockData.pricing.intro}</p>
          
          <div className="pricing-groups">
            <Card className="pricing-group-card glass-card">
              <CardHeader>
                <CardTitle className="pricing-group-title">{mockData.pricing.bookkeeping.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="pricing-tiers">
                  {mockData.pricing.bookkeeping.tiers.map((tier, idx) => (
                    <div key={idx} className="pricing-tier">
                      <span className="tier-name">{tier.name}</span>
                      <span className="tier-price">{tier.price}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="pricing-group-card glass-card">
              <CardHeader>
                <CardTitle className="pricing-group-title">{mockData.pricing.payApp.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="pricing-tiers">
                  {mockData.pricing.payApp.tiers.map((tier, idx) => (
                    <div key={idx} className="pricing-tier">
                      <span className="tier-name">{tier.name}</span>
                      <span className="tier-price">{tier.price}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <p className="pricing-footnote">{mockData.pricing.footnote}</p>
          
          <div className="pricing-cta">
            <Button onClick={() => setIsCalendlyOpen(true)} size="lg" className="cta-button cta-large">
              {mockData.pricing.ctaPrimary}
            </Button>
            <Button 
              onClick={() => setIsEstimateOpen(true)} 
              size="lg" 
              variant="outline" 
              className="cta-secondary cta-large"
            >
              {mockData.pricing.ctaSecondary}
            </Button>
          </div>
        </div>
      </section>

      <section id="lead-magnet" className="lead-magnet-section">
        <div className="container-custom">
          <Card className="lead-magnet-card glass-card">
            <CardHeader>
              <CardTitle className="lead-magnet-title">{mockData.leadMagnet.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="lead-magnet-description">{mockData.leadMagnet.description}</p>
              <form onSubmit={handleChecklistSubmit} className="checklist-form">
                <Input
                  type="email"
                  placeholder="Enter your work email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="email-input"
                />
                <Button type="submit" className="cta-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : mockData.leadMagnet.buttonText}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <section id="faq" className="faq-section">
        <div className="container-custom">
          <h2 className="section-title">{mockData.faq.title}</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            {mockData.faq.items.map((item, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="faq-item">
                <AccordionTrigger className="faq-question">{item.question}</AccordionTrigger>
                <AccordionContent className="faq-answer">{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <footer id="footer" className="footer-section">
        <div className="container-custom">
          <div className="footer-content">
            <div className="footer-main">
              <div className="footer-logo">
                <Building2 className="logo-icon" />
                <span className="logo-text">{mockData.header.logo}</span>
              </div>
              <div className="footer-tagline">{mockData.footer.tagline}</div>
            </div>
            <div className="footer-contact">
              <a href={`mailto:${mockData.footer.email}`} className="footer-link">
                <Mail className="inline-icon" /> {mockData.footer.email}
              </a>
              <a href={`tel:${mockData.footer.phone.replace(/[^0-9]/g, '')}`} className="footer-link">
                <Phone className="inline-icon" /> {mockData.footer.phone}
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="copyright">{mockData.footer.copyright}</p>
          </div>
        </div>
      </footer>

      <Dialog open={isCalendlyOpen} onOpenChange={setIsCalendlyOpen}>
        <DialogContent className="calendly-modal" onEscapeKeyDown={() => setIsCalendlyOpen(false)} onInteractOutside={() => setIsCalendlyOpen(false)}>
          <DialogHeader>
            <DialogTitle>Book Your 20-Minute Fit Call</DialogTitle>
          </DialogHeader>
          <div className="calendly-embed">
            <iframe
              src={CALENDLY_URL}
              width="100%"
              height="700"
              frameBorder="0"
              title="Book Fit Call"
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isClientLoginOpen} onOpenChange={setIsClientLoginOpen}>
        <DialogContent className="modal-content">
          <DialogHeader>
            <DialogTitle>{mockData.modals.clientLogin.headline}</DialogTitle>
            <DialogDescription>{mockData.modals.clientLogin.body}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClientLoginSubmit} className="modal-form">
            <Input
              type="email"
              placeholder="Email (required)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="email-input"
            />
            <Input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
              className="email-input"
            />
            <Button type="submit" className="cta-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : mockData.modals.clientLogin.buttonText}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEstimateOpen} onOpenChange={setIsEstimateOpen}>
        <DialogContent className="modal-content">
          <DialogHeader>
            <DialogTitle>Get Your Estimate by Email</DialogTitle>
            <DialogDescription>Tell us your email and we'll send you a customized estimate.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEstimateSubmit} className="modal-form">
            <Input
              type="email"
              placeholder="Enter your work email"
              value={estimateEmail}
              onChange={(e) => setEstimateEmail(e.target.value)}
              required
              disabled={isSubmitting}
              className="email-input"
            />
            <Button type="submit" className="cta-button" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send My Estimate'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isChecklistThankYouOpen} onOpenChange={setIsChecklistThankYouOpen}>
        <DialogContent className="modal-content">
          <DialogHeader>
            <DialogTitle>{mockData.modals.checklistThankYou.headline}</DialogTitle>
            <DialogDescription>{mockData.modals.checklistThankYou.body}</DialogDescription>
          </DialogHeader>
          <Button 
            onClick={() => {
              setIsChecklistThankYouOpen(false);
              setIsCalendlyOpen(true);
            }} 
            className="cta-button"
          >
            {mockData.modals.checklistThankYou.buttonText}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={isEstimateThankYouOpen} onOpenChange={setIsEstimateThankYouOpen}>
        <DialogContent className="modal-content">
          <DialogHeader>
            <DialogTitle>{mockData.modals.estimateThankYou.headline}</DialogTitle>
            <DialogDescription>{mockData.modals.estimateThankYou.body}</DialogDescription>
          </DialogHeader>
          <Button 
            onClick={() => {
              setIsEstimateThankYouOpen(false);
              setIsCalendlyOpen(true);
            }} 
            className="cta-button"
          >
            {mockData.modals.estimateThankYou.buttonText}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
